/**
 * F-603-FE-02: ConnectionStatus
 *
 * Non-blocking connection status banner displayed at the top of session views.
 *
 * States:
 *  - Reconnecting: amber "Connecting..." banner with pulsing dot animation.
 *    Appears when a previously-connected WebSocket has dropped and reconnection
 *    is in progress. Does not block user interaction.
 *  - Reconnected: green "Reconnected. Session resumed." toast that auto-dismisses
 *    after 3 seconds.
 *  - Session ended while disconnected: neutral informational message (passed as prop
 *    from the consuming page that receives SESSION_ENDED / SESSION_REJOINED with
 *    non-active status).
 *
 * Usage:
 *   <ConnectionStatus
 *     isReconnecting={isReconnecting}
 *     justReconnected={justReconnected}
 *     sessionEndedWhileDisconnected={sessionEndedWhileDisconnected}
 *   />
 *
 * Place this component at the top of the session page's JSX tree (before header
 * or overlaid at the top with fixed/sticky positioning).
 */

import { type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ConnectionStatusProps {
  /** True while WebSocket reconnection is in progress. */
  isReconnecting: boolean;
  /** True for ~3 seconds after a successful reconnection. */
  justReconnected: boolean;
  /** True when SESSION_REJOINED / SESSION_ENDED reveals the session closed while offline. */
  sessionEndedWhileDisconnected?: boolean;
}

// ---------------------------------------------------------------------------
// Reusable banner wrapper
// ---------------------------------------------------------------------------

interface BannerProps {
  children: ReactNode;
  className: string;
  role?: 'status' | 'alert';
}

function Banner({ children, className, role = 'status' }: BannerProps) {
  return (
    <div
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className={`w-full px-4 py-2 flex items-center justify-center gap-2 text-sm font-body font-medium ${className}`}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ConnectionStatus({
  isReconnecting,
  justReconnected,
  sessionEndedWhileDisconnected = false,
}: ConnectionStatusProps) {
  // "Session ended while disconnected" takes highest priority
  if (sessionEndedWhileDisconnected) {
    return (
      <Banner
        className="bg-clinical-charcoal/90 text-white"
        role="status"
      >
        <svg
          className="w-4 h-4 flex-shrink-0 text-white/70"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
        Session ended while disconnected.
      </Banner>
    );
  }

  // "Reconnected. Session resumed." — auto-dismisses after 3s (managed by context)
  if (justReconnected) {
    return (
      <Banner
        className="bg-emerald-600 text-white"
        role="status"
      >
        <svg
          className="w-4 h-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Reconnected. Session resumed.
      </Banner>
    );
  }

  // "Connecting..." — shown while reconnection is in progress
  if (isReconnecting) {
    return (
      <Banner
        className="bg-amber-50 border-b border-amber-200 text-amber-800"
        role="status"
      >
        {/* Pulsing dot */}
        <span
          className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0"
          aria-hidden="true"
        />
        Connecting...
      </Banner>
    );
  }

  // Nothing to show — render null (no DOM element)
  return null;
}
